import {z} from 'zod'

import {
    customerContactRefRawSchema,
    customerContactRefSchema,
    customerContactSourceSchema,
} from '../customers/schemas'
import {mapPartialPickup, partialPickupRawSchema} from '../work/partial-pickup-schema'
import {
    jobStockEquipmentGroupRawSchema,
    jobStockEquipmentGroupSchema,
} from './stock-equipment/schemas'

const jobStatusSchema = z.enum(['planned', 'active', 'completed', 'cancelled'])

const jobScheduleRawSchema = z.object({
    start_date: z.string(),
    start_time: z.string().nullable(),
    end_date: z.string().nullable(),
    end_time: z.string().nullable(),
})

const jobLocationRawSchema = z.object({
    id: z.string(),
    location_id: z.string(),
    name: z.string(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    notes: z.string().nullable(),
    saved_location_id: z.string().nullable().optional(),
    full_address: z.string().nullable().optional(),
})

export const jobWorkerRequirementRawSchema = z.object({
    id: z.string(),
    worker_type_id: z.string(),
    worker_type_name: z.string(),
    quantity: z.number(),
    billing_rate: z.string().nullable().optional(),
    billing_type: z
        .enum(['hourly', 'daily', 'weekly', 'monthly', 'flat', 'per_load'])
        .nullable()
        .optional(),
    estimated_hours: z.string().nullable().optional(),
})

export const jobAssetRequirementRawSchema = z.object({
    id: z.string(),
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    quantity: z.number(),
    billing_rate: z.string().nullable().optional(),
    billing_type: z
        .enum(['hourly', 'daily', 'weekly', 'monthly', 'flat', 'per_load'])
        .nullable()
        .optional(),
})

export const jobWorkerAssignmentRawSchema = jobScheduleRawSchema.extend({
    id: z.string(),
    worker_id: z.string(),
    worker_name: z.string(),
})

export const jobAssetAssignmentRawSchema = jobScheduleRawSchema.extend({
    id: z.string(),
    rental_asset_id: z.string(),
    rental_asset_name: z.string(),
    rental_asset_type_id: z.string(),
    is_uniquely_identified: z.literal(true).optional().default(true),
    notes: z.string().nullable(),
    is_third_party: z.boolean().optional(),
    unit_number: z.string().nullable().optional(),
    has_unit_numbers: z.boolean().optional(),
    pending_on_rent: z.boolean().optional(),
})

const billableChargeTypeSchema = z.enum([
    'transportation_hours',
    'per_load',
    'flat_rate',
    'disposal',
    'washout',
    'demurrage',
    'hsei',
    'rental',
    'manual',
])

export const projectBillableLineItemRawSchema = z.object({
    id: z.string(),
    charge_type: billableChargeTypeSchema,
    name: z.string(),
    description: z.string(),
    quantity: z.string(),
    unit: z.string(),
    unit_price: z.string(),
    total_price: z.string(),
    invoice_id: z.string().nullable(),
    is_synced: z.boolean(),
    po_number: z.string().nullable().optional(),
    project_billing_terms_id: z.string().nullable().optional(),
})

export const jobCommentRawSchema = z.object({
    id: z.string(),
    content: z.string(),
    created_at: z.string(),
    created_by_id: z.string(),
    created_by_name: z.string(),
    updated_at: z.string().nullable(),
    updated_by_id: z.string().nullable(),
    updated_by_name: z.string().nullable(),
})

export const jobCommentCreateInputSchema = z.object({
    content: z.string().min(1, 'Comment content cannot be empty'),
})

export const jobCommentUpdateInputSchema = z.object({
    content: z.string().min(1, 'Comment content cannot be empty'),
})

export const jobListItemRawSchema = jobScheduleRawSchema.extend({
    date_tbc: z.boolean().optional(),
    id: z.string(),
    project_id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    service_type_id: z.string(),
    service_type_name: z.string(),
    notes: z.string().nullable(),
    po_number: z.string().nullable().optional(),
    project_billing_terms_id: z.string().optional(),
    status: jobStatusSchema,
    customer_id: z.string(),
    customer_name: z.string(),
    display_id: z.string().min(7).max(15),
    created_at: z.string(),
    partial_pickup: partialPickupRawSchema.nullable().optional(),
})

export const jobDetailRawSchema = jobScheduleRawSchema.extend({
    date_tbc: z.boolean().optional(),
    id: z.string(),
    project_id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    service_type_id: z.string(),
    service_type_name: z.string(),
    notes: z.string().nullable(),
    po_number: z.string().nullable().optional(),
    project_billing_terms_id: z.string().optional(),
    billing_method: z
        .enum(['line_rates', 'hourly_transportation', 'flat_rate', 'per_load'])
        .nullable()
        .optional(),
    status: jobStatusSchema,
    customer_id: z.string(),
    customer_name: z.string(),
    display_id: z.string().min(7).max(15),
    created_at: z.string(),
    customer_contact: customerContactRefRawSchema.nullable().optional(),
    customer_contact_source: customerContactSourceSchema.nullable().optional(),
    service_schedule_id: z.string().nullable().optional(),
    schedule_role: z.enum(['deploy', 'service', 'pickup']).nullable().optional(),
    book_by_day: z.boolean().optional(),
    specific_type: z.string().nullable().optional(),
    planned_end_date: z.string().nullable().optional(),
    cancel_reason: z.string().nullable().optional(),
    did_not_happen_at: z.string().nullable().optional(),
    did_not_happen_by_name: z.string().nullable().optional(),
    project_name: z.string().nullable().optional(),
    project_job_count: z.number().optional(),
    order_number: z.string().nullable().optional(),
    rep_id: z.string().nullable().optional(),
    rep_name: z.string().nullable().optional(),
    project_created_by_id: z.string().nullable().optional(),
    created_by_id: z.string().nullable().optional(),
    is_pickup: z.boolean().optional(),
    partial_pickup: partialPickupRawSchema.nullable().optional(),
    pickup_confirmed_at: z.string().nullable().optional(),
    pickup_confirmed_by_name: z.string().nullable().optional(),
    warnings: z.array(z.string()).optional(),
    locations: z.array(jobLocationRawSchema),
    worker_requirements: z.array(jobWorkerRequirementRawSchema),
    asset_requirements: z.array(jobAssetRequirementRawSchema),
    worker_assignments: z.array(jobWorkerAssignmentRawSchema),
    asset_assignments: z.array(jobAssetAssignmentRawSchema),
    stock_equipment_groups: z.array(jobStockEquipmentGroupRawSchema).optional(),
    billable_line_items: z.array(projectBillableLineItemRawSchema),
    comments: z.array(jobCommentRawSchema),
})

function mapSchedule(raw: z.infer<typeof jobScheduleRawSchema>) {
    return {
        startDate: raw.start_date,
        startTime: raw.start_time,
        endDate: raw.end_date,
        endTime: raw.end_time,
    }
}

export const jobListItemSchema = jobListItemRawSchema.transform((raw) => ({
    id: raw.id,
    projectId: raw.project_id,
    name: raw.name,
    description: raw.description,
    serviceTypeId: raw.service_type_id,
    serviceTypeName: raw.service_type_name,
    notes: raw.notes,
    poNumber: raw.po_number ?? null,
    projectBillingTermsId: raw.project_billing_terms_id ?? null,
    status: raw.status,
    customerId: raw.customer_id,
    customerName: raw.customer_name,
    displayId: raw.display_id,
    createdAt: raw.created_at,
    ...mapSchedule(raw),
    ...(raw.date_tbc ? {dateTbc: true} : {}),
    ...(raw.partial_pickup ? {partialPickup: mapPartialPickup(raw.partial_pickup)} : {}),
}))

export const projectBillableLineItemSchema = projectBillableLineItemRawSchema.transform((raw) => ({
    id: raw.id,
    chargeType: raw.charge_type,
    name: raw.name,
    description: raw.description,
    quantity: Number(raw.quantity),
    unit: raw.unit,
    unitPrice: Number(raw.unit_price),
    totalPrice: Number(raw.total_price),
    invoiceId: raw.invoice_id,
    isSynced: raw.is_synced,
    poNumber: raw.po_number ?? null,
    projectBillingTermsId: raw.project_billing_terms_id ?? null,
}))

export const jobDetailSchema = jobDetailRawSchema.transform((raw) => ({
    id: raw.id,
    projectId: raw.project_id,
    name: raw.name,
    description: raw.description,
    serviceTypeId: raw.service_type_id,
    serviceTypeName: raw.service_type_name,
    notes: raw.notes,
    poNumber: raw.po_number ?? null,
    projectBillingTermsId: raw.project_billing_terms_id ?? null,
    billingMethod: raw.billing_method ?? null,
    status: raw.status,
    customerId: raw.customer_id,
    customerName: raw.customer_name,
    displayId: raw.display_id,
    createdAt: raw.created_at,
    customerContact:
        raw.customer_contact == null ? null : customerContactRefSchema.parse(raw.customer_contact),
    customerContactSource: raw.customer_contact_source ?? null,
    serviceScheduleId: raw.service_schedule_id ?? null,
    scheduleRole: raw.schedule_role ?? null,
    ...mapSchedule(raw),
    ...(raw.date_tbc ? {dateTbc: true} : {}),
    ...(raw.book_by_day ? {bookByDay: true} : {}),
    specificType: raw.specific_type ?? null,
    plannedEndDate: raw.planned_end_date ?? null,
    ...(raw.cancel_reason ? {cancelReason: raw.cancel_reason} : {}),
    ...(raw.did_not_happen_at ? {didNotHappenAt: raw.did_not_happen_at} : {}),
    ...(raw.did_not_happen_by_name ? {didNotHappenByName: raw.did_not_happen_by_name} : {}),
    ...(raw.warnings?.length ? {warnings: raw.warnings} : {}),
    projectName: raw.project_name ?? null,
    projectJobCount: raw.project_job_count ?? 0,
    orderNumber: raw.order_number ?? null,
    repId: raw.rep_id ?? null,
    repName: raw.rep_name ?? null,
    projectCreatedById: raw.project_created_by_id ?? null,
    createdById: raw.created_by_id ?? null,
    isPickup: raw.is_pickup ?? false,
    ...(raw.partial_pickup ? {partialPickup: mapPartialPickup(raw.partial_pickup)} : {}),
    pickupConfirmedAt: raw.pickup_confirmed_at ?? null,
    pickupConfirmedByName: raw.pickup_confirmed_by_name ?? null,
    locations: raw.locations.map((location) => ({
        id: location.id,
        locationId: location.location_id,
        name: location.name,
        latitude: location.latitude,
        longitude: location.longitude,
        notes: location.notes,
        savedLocationId: location.saved_location_id ?? null,
        ...(location.full_address ? {addressText: location.full_address} : {}),
    })),
    workerRequirements: raw.worker_requirements.map((requirement) => ({
        id: requirement.id,
        workerTypeId: requirement.worker_type_id,
        workerTypeName: requirement.worker_type_name,
        quantity: requirement.quantity,
        billingRate: requirement.billing_rate ?? null,
        billingType: requirement.billing_type ?? null,
        estimatedHours:
            requirement.estimated_hours != null ? Number(requirement.estimated_hours) : null,
    })),
    assetRequirements: raw.asset_requirements.map((requirement) => ({
        id: requirement.id,
        rentalAssetTypeId: requirement.rental_asset_type_id,
        rentalAssetTypeName: requirement.rental_asset_type_name,
        quantity: requirement.quantity,
        billingRate: requirement.billing_rate,
        billingType: requirement.billing_type,
    })),
    workerAssignments: raw.worker_assignments.map((assignment) => ({
        id: assignment.id,
        workerId: assignment.worker_id,
        workerName: assignment.worker_name,
        ...mapSchedule(assignment),
    })),
    stockEquipmentGroups: (raw.stock_equipment_groups ?? []).map((group) =>
        jobStockEquipmentGroupSchema.parse(group),
    ),
    assetAssignments: raw.asset_assignments.map((assignment) => ({
        id: assignment.id,
        rentalAssetId: assignment.rental_asset_id,
        rentalAssetName: assignment.rental_asset_name,
        rentalAssetTypeId: assignment.rental_asset_type_id,
        isUniquelyIdentified: assignment.is_uniquely_identified,
        notes: assignment.notes,
        isThirdParty: assignment.is_third_party ?? false,
        unitNumber: assignment.unit_number ?? null,
        hasUnitNumbers: assignment.has_unit_numbers ?? true,
        pendingOnRent: assignment.pending_on_rent ?? false,
        ...mapSchedule(assignment),
    })),
    billableLineItems: raw.billable_line_items.map((lineItem) =>
        projectBillableLineItemSchema.parse(lineItem),
    ),
    comments: raw.comments.map((comment) => ({
        id: comment.id,
        content: comment.content,
        createdAt: comment.created_at,
        createdById: comment.created_by_id,
        createdByName: comment.created_by_name,
        updatedAt: comment.updated_at,
        updatedById: comment.updated_by_id,
        updatedByName: comment.updated_by_name,
    })),
}))

export type JobStatus = z.infer<typeof jobStatusSchema>
export type JobListItemRaw = z.infer<typeof jobListItemRawSchema>
export type JobListItem = z.infer<typeof jobListItemSchema>
export type JobDetailRaw = z.infer<typeof jobDetailRawSchema>
export type JobDetail = z.infer<typeof jobDetailSchema>
export type ProjectBillableLineItem = z.infer<typeof projectBillableLineItemSchema>
export type BillableChargeType = z.infer<typeof billableChargeTypeSchema>

export type JobListSortBy = 'start_date' | 'created_at'
export type JobListSortDir = 'asc' | 'desc'
export type JobListStatusFilter = JobStatus

export type JobListFilters = {
    mine?: boolean
    projectId?: string
    customerId?: string
    name?: string
    startDateFrom?: string | Date | null
    startDateTo?: string | Date | null
    status?: JobListStatusFilter[]
    sortBy?: JobListSortBy
    sortDir?: JobListSortDir
}

export const jobWorkerRequirementInputSchema = z.object({
    id: z.string().optional(),
    workerTypeId: z.string(),
    quantity: z.number(),
    billingRate: z.string().nullable().optional(),
    billingType: z
        .enum(['hourly', 'daily', 'weekly', 'monthly', 'flat', 'per_load'])
        .nullable()
        .optional(),
})

export type JobWorkerRequirementInput = z.infer<typeof jobWorkerRequirementInputSchema>

export const jobAssetRequirementInputSchema = z.object({
    id: z.string().optional(),
    rentalAssetTypeId: z.string(),
    quantity: z.number(),
    billingRate: z.string().optional(),
    billingType: z.enum(['hourly', 'daily', 'weekly', 'monthly', 'per_load']).optional(),
})

export type JobAssetRequirementInput = z.infer<typeof jobAssetRequirementInputSchema>

export const jobWorkerAssignmentInputSchema = z.object({
    id: z.string().optional(),
    workerId: z.string(),
    startDate: z.string(),
    startTime: z.string().nullable(),
    endDate: z.string().nullable(),
    endTime: z.string().nullable(),
})

export type JobWorkerAssignmentInput = z.infer<typeof jobWorkerAssignmentInputSchema>

export const jobAssetAssignmentInputSchema = z.object({
    id: z.string().optional(),
    rentalAssetId: z.string().optional(),
    thirdPartyRentalAssetOfferingId: z.string().optional(),
    quantity: z.number().int().min(1).optional(),
    startDate: z.string(),
    startTime: z.string().nullable(),
    endDate: z.string().nullable(),
    endTime: z.string().nullable(),
    notes: z.string().nullable().optional(),
})

export type JobAssetAssignmentInput = z.infer<typeof jobAssetAssignmentInputSchema>

export const createBillableLineItemInputSchema = z.object({
    chargeType: billableChargeTypeSchema,
    name: z.string(),
    description: z.string().optional(),
    quantity: z.number(),
    unit: z.string(),
    unitPrice: z.number(),
    totalPrice: z.number(),
})

export type CreateBillableLineItemInput = z.infer<typeof createBillableLineItemInputSchema>

export const createBillableLineItemRequestSchema = createBillableLineItemInputSchema.transform(
    ({chargeType, name, description, quantity, unit, unitPrice, totalPrice}) => ({
        charge_type: chargeType,
        name: name.trim(),
        description: description?.trim() ?? '',
        quantity: quantity.toFixed(2),
        unit,
        unit_price: unitPrice.toFixed(4),
        total_price: totalPrice.toFixed(2),
    }),
)

export const jobPatchInputSchema = z.object({
    name: z.string().optional(),
    description: z.string().optional(),
    serviceTypeId: z.string().optional(),
    notes: z.string().nullable().optional(),
    customerContactId: z.string().nullable().optional(),
    startDate: z.string().optional(),
    startTime: z.string().nullable().optional(),
    endDate: z.string().nullable().optional(),
    endTime: z.string().nullable().optional(),
    status: jobStatusSchema.optional(),
    cancelReason: z.string().optional(),
    bookByDay: z.boolean().optional(),
    poNumber: z.string().nullable().optional(),
    projectBillingTermsId: z.string().nullable().optional(),
    specificType: z.string().nullable().optional(),
    plannedEndDate: z.string().nullable().optional(),
})

export type JobPatchInput = z.infer<typeof jobPatchInputSchema>

export const jobPatchRequestSchema = jobPatchInputSchema.transform(
    ({
        name,
        description,
        serviceTypeId,
        notes,
        customerContactId,
        startDate,
        startTime,
        endDate,
        endTime,
        status,
        cancelReason,
        bookByDay,
        poNumber,
        projectBillingTermsId,
        specificType,
        plannedEndDate,
    }) => ({
        ...(name != null ? {name: name.trim()} : {}),
        ...(description != null ? {description: description.trim()} : {}),
        ...(serviceTypeId != null ? {service_type_id: serviceTypeId} : {}),
        ...(notes !== undefined ? {notes: notes?.trim() || null} : {}),
        ...(customerContactId !== undefined ? {customer_contact_id: customerContactId} : {}),
        ...(startDate != null ? {start_date: startDate} : {}),
        ...(startTime !== undefined ? {start_time: startTime} : {}),
        ...(endDate !== undefined ? {end_date: endDate} : {}),
        ...(endTime !== undefined ? {end_time: endTime} : {}),
        ...(status != null ? {status} : {}),
        ...(cancelReason != null ? {cancel_reason: cancelReason.trim()} : {}),
        ...(bookByDay != null ? {book_by_day: bookByDay} : {}),
        ...(poNumber !== undefined ? {po_number: poNumber?.trim() || null} : {}),
        ...(projectBillingTermsId !== undefined
            ? {project_billing_terms_id: projectBillingTermsId}
            : {}),
        ...(specificType !== undefined ? {specific_type: specificType?.trim() || null} : {}),
        ...(plannedEndDate !== undefined ? {planned_end_date: plannedEndDate} : {}),
    }),
)

export const timeEntryRawSchema = z.object({
    id: z.string(),
    start_date: z.string(),
    start_time: z.string(),
    end_date: z.string(),
    end_time: z.string(),
    created_at: z.string(),
    duration_hours: z.number(),
})

export const workerAssignmentTimeEntriesRawSchema = z.object({
    assignment_id: z.string(),
    worker_id: z.string(),
    worker_name: z.string(),
    entries: z.array(timeEntryRawSchema),
})

export const timeEntrySchema = timeEntryRawSchema.transform((raw) => ({
    id: raw.id,
    startDate: raw.start_date,
    startTime: raw.start_time,
    endDate: raw.end_date,
    endTime: raw.end_time,
    createdAt: raw.created_at,
    durationHours: raw.duration_hours,
}))

export const workerAssignmentTimeEntriesSchema = workerAssignmentTimeEntriesRawSchema.transform(
    (raw) => ({
        assignmentId: raw.assignment_id,
        workerId: raw.worker_id,
        workerName: raw.worker_name,
        entries: raw.entries.map((entry) => timeEntrySchema.parse(entry)),
    }),
)

export const timeEntryInputSchema = z.object({
    startDate: z.string(),
    startTime: z.string(),
    endDate: z.string(),
    endTime: z.string(),
})

export type TimeEntryInput = z.infer<typeof timeEntryInputSchema>

export const timeEntryRequestSchema = timeEntryInputSchema.transform(
    ({startDate, startTime, endDate, endTime}) => ({
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime,
    }),
)

export type TimeEntry = z.infer<typeof timeEntrySchema>
export type WorkerAssignmentTimeEntries = z.infer<typeof workerAssignmentTimeEntriesSchema>
export type TimeEntryRaw = z.infer<typeof timeEntryRawSchema>
export type JobCommentRaw = z.infer<typeof jobCommentRawSchema>
export type JobCommentCreateInput = z.infer<typeof jobCommentCreateInputSchema>
export type JobCommentUpdateInput = z.infer<typeof jobCommentUpdateInputSchema>
export type WorkerAssignmentTimeEntriesRaw = z.infer<typeof workerAssignmentTimeEntriesRawSchema>
